import { inject, Injectable } from '@angular/core';
import {
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthGuardService {
  private router = inject(Router);

  /** True when a non-empty stored token passes validation (e.g. unexpired JWT). */
  hasValidStoredToken(): boolean {
    const token = this.getToken();
    return !!token && this.isTokenValid(token);
  }

  canActivate(): boolean {
    const token = this.getToken();

    if (!token) {
      this.redirectToLogin();
      return false;
    }

    if (!this.isTokenValid(token)) {
      this.clearToken();
      this.redirectToLogin();
      return false;
    }

    return true;
  }

  private getToken(): string | null {
    return localStorage.getItem('regen_access_token');
  }

  private isTokenValid(token: string): boolean {
    try {
      if (token.length === 0) {
        return false;
      }

      if (this.isJWTToken(token)) {
        return this.isJWTTokenValid(token);
      }

      return true;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }

  private isJWTToken(token: string): boolean {
    return token.split('.').length === 3;
  }

  private isJWTTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < currentTime) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error parsing JWT token:', error);
      return false;
    }
  }

  clearToken(): void {
    // Must match the key used everywhere else (http interceptor, user state, etc.)
    localStorage.removeItem('regen_access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('authToken');
  }

  private redirectToLogin(): void {
    this.router.navigate(['/login']);
  }
}

// Functional guard (recommended approach)
export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): boolean => {
  return inject(AuthGuardService).canActivate();
};

// Enhanced functional guard with redirect URL preservation
export const authGuardWithRedirect: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): boolean => {
  const authService = inject(AuthGuardService);
  const router = inject(Router);

  if (!authService.hasValidStoredToken()) {
    localStorage.setItem('redirectUrl', state.url);
    authService.clearToken();
    router.navigate(['/login']);
    return false;
  }

  return true;
};
